import { call, put, takeLatest, select } from "redux-saga/effects";
import {
  getHotlistsSuccess,
  getHotlistsFailure,
  addHotlistSuccess,
  addHotlistFailure,
  getHotlists,
  addHotlist,
  deleteHotlistRecord,
  deleteHotlistRecordSuccess,
  deleteHotlistRecordFailure,
  deleteHotlistRecords,
  deleteHotlistRecordsSuccess,
  deleteHotlistRecordsFailure,
  deleteHotlist,
  deleteHotlistSuccess,
  deleteHotlistFailure,
} from "./hotlistSlice";
import {
  fetchHotlistsApi,
  addHotlistApi,
  updateHotlistApi,
  deleteHotlistApi,
  getAllUsersApi,
} from "./hotlistApi";
import { showToast } from "../../../components";

function* fetchHotlistsSaga(action) {
  try {
    const currentUser = yield select((state) => state.auth.user?.data);
    const currentUserUiid = currentUser?.uiid;

    const getReportToId = (user) => {
      const rt = user?.reportTo;
      if (!rt) return null;
      if (Array.isArray(rt)) {
        const firstItem = rt[0];
        return firstItem?.uiid || firstItem;
      }
      return rt?.uiid || rt;
    };

    const currentUserReportTo = getReportToId(currentUser);

    if (currentUserUiid) {
      // 2. Fetch All Users to determine Team
      const usersResponse = yield call(getAllUsersApi);
      let teamIds = [];

      if (usersResponse?.data?.data) {
        const allUsers = usersResponse.data.data;

        teamIds = allUsers
          .filter((u) => {
            const uReportTo = getReportToId(u);
            const uUiid = u.uiid;
            const isPeer =
              uReportTo &&
              currentUserReportTo &&
              uReportTo === currentUserReportTo;
            const isDirectReport = uReportTo === currentUserUiid;
            return isPeer || isDirectReport;
          })
          .map((u) => u.uiid);
      }

      if (!teamIds.includes(currentUserUiid)) {
        teamIds.push(currentUserUiid);
      }
      const visibilityQuery = {
        $or: [
          { visibility: 1 },
          { visibility: 2, addedBy: { $in: teamIds } },
          { visibility: 3, addedBy: currentUserUiid },
        ],
      };

      const newPayload = {
        ...action.payload,
        q: JSON.stringify(visibilityQuery),
      };

      const response = yield call(fetchHotlistsApi, newPayload);
      if (response?.data) {
        const hotlistData = response.data.data || [];
        const total = response.data.total || response.data.count || 0;
        yield put(getHotlistsSuccess({ data: hotlistData, total }));
      } else {
        yield put(
          getHotlistsFailure(response.error || "Failed to fetch hotlists"),
        );
      }
    } else {
      const response = yield call(fetchHotlistsApi, action.payload);
      if (response?.data) {
        const hotlistData = response.data.data || [];
        const total = response.data.total || response.data.count || 0;
        yield put(getHotlistsSuccess({ data: hotlistData, total }));
      } else {
        yield put(
          getHotlistsFailure(response.error || "Failed to fetch hotlists"),
        );
      }
    }
  } catch (error) {
    yield put(getHotlistsFailure(error.message));
  }
}

function* addHotlistSaga(action) {
  try {
    const { payload, onSuccess, onFailure } = action.payload;
    const response = yield call(addHotlistApi, payload);
    
    if (response.success) {
      yield put(addHotlistSuccess());
      yield put(getHotlists());
      if (onSuccess) onSuccess();
    } else {
      yield put(addHotlistFailure(response.error || "Failed to add hotlist"));
      if (onFailure) onFailure(response.error || "Failed to add hotlist");
    }
  } catch (error) {
    yield put(addHotlistFailure(error.message));
    if (action.payload.onFailure) {
      action.payload.onFailure(error.message);
    }
  }
}

function* deleteHotlistRecordSaga(action) {
  try {
    const { hotlistId, recordId, onSuccess, onFailure } = action.payload;

    const apiPayload = {
      uiid: hotlistId,
      payload: {
        $pull: {
          records: { _id: recordId },
        },
      },
    };

    const response = yield call(updateHotlistApi, apiPayload);

    if (response.success || response.data?.success) {
      yield put(deleteHotlistRecordSuccess());
      yield put(getHotlists());
      showToast("Record deleted successfully", "success");
      if (onSuccess) onSuccess();
    } else {
      const errorMsg =
        response.error || response.data?.error || "Failed to delete record";
      yield put(deleteHotlistRecordFailure(errorMsg));
      showToast(errorMsg, "error");
      if (onFailure) onFailure(errorMsg);
    }
  } catch (error) {
    yield put(deleteHotlistRecordFailure(error.message));
    showToast(error.message, "error");
    if (action.payload?.onFailure) {
      action.payload.onFailure(error.message);
    }
  }
}

function* deleteHotlistRecordsSaga(action) {
  try {
    const { hotlistId, recordIds, recordsContent, onSuccess, onFailure } =
      action.payload;

    // Build pull match conditions for each selected record
    let pullConditions = [];

    // Records with _id: match by _id
    if (recordIds && recordIds.length > 0) {
      recordIds.forEach((id) => pullConditions.push({ _id: id }));
    }

    // Records without _id: match by content fields
    if (recordsContent && recordsContent.length > 0) {
      pullConditions.push(...recordsContent);
    }

    if (pullConditions.length === 0) {
      if (onSuccess) onSuccess();
      return;
    }

    // Build the $pull query matching the SSOT curl format
    const pullQuery =
      pullConditions.length === 1 ? pullConditions[0] : { $or: pullConditions };

    const apiPayload = {
      uiid: hotlistId,
      payload: {
        $pull: {
          records: pullQuery,
        },
      },
    };

    const response = yield call(updateHotlistApi, apiPayload);

    if (response.success || response.data?.success) {
      yield put(deleteHotlistRecordsSuccess());
      yield put(getHotlists());
      const totalDeleted =
        (recordIds?.length || 0) + (recordsContent?.length || 0);
      showToast(`${totalDeleted} record(s) deleted successfully`, "success");
      if (onSuccess) onSuccess();
    } else {
      const errorMsg =
        response.error || response.data?.error || "Failed to delete records";
      yield put(deleteHotlistRecordsFailure(errorMsg));
      showToast(errorMsg, "error");
      if (onFailure) onFailure(errorMsg);
    }
  } catch (error) {
    yield put(deleteHotlistRecordsFailure(error.message));
    showToast(error.message, "error");
    if (action.payload?.onFailure) {
      action.payload.onFailure(error.message);
    }
  }
}

function* deleteHotlistSaga(action) {
  try {
    const { hotlistId, onSuccess, onFailure } = action.payload;
    const response = yield call(deleteHotlistApi, { uiid: hotlistId });

    if (response.success || response.data?.success) {
      yield put(deleteHotlistSuccess());
      yield put(getHotlists());
      showToast("Hotlist deleted successfully", "success");
      if (onSuccess) onSuccess();
    } else {
      const errorMsg = response.error || "Failed to delete hotlist";
      yield put(deleteHotlistFailure(errorMsg));
      showToast(errorMsg, "error");
      if (onFailure) onFailure(errorMsg);
    }
  } catch (error) {
    yield put(deleteHotlistFailure(error.message));
    showToast(error.message, "error");
  }
}

export function* watchHotlist() {
  yield takeLatest(getHotlists.type, fetchHotlistsSaga);
  yield takeLatest(addHotlist.type, addHotlistSaga);
  yield takeLatest(deleteHotlistRecord.type, deleteHotlistRecordSaga);
  yield takeLatest(deleteHotlistRecords.type, deleteHotlistRecordsSaga);
  yield takeLatest(deleteHotlist.type, deleteHotlistSaga);
}
